// src/components/math/MathUtils.jsx
export class MathUtils {
  static mathJaxLoaded = false;
  static mathJaxPromise = null;
  static mathLiveLoaded = false;
  static mathLivePromise = null;

  static initializeMathJax() {
    if (this.mathJaxLoaded) return Promise.resolve();
    if (this.mathJaxPromise) return this.mathJaxPromise;

    this.mathJaxPromise = new Promise((resolve, reject) => {
      if (window.MathJax && window.MathJax.typesetPromise) {
        this.mathJaxLoaded = true;
        resolve();
        return;
      }

      window.MathJax = {
        tex: {
          inlineMath: [['\\(', '\\)'], ['$', '$']],
          displayMath: [['\\[', '\\]'], ['$$', '$$']],
          processEscapes: true,
          processEnvironments: true
        },
        svg: {
          fontCache: 'global',
          scale: 0.95,
          matchFontHeight: true
        }
      };

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js';
      script.async = true;
      script.onload = () => {
        console.log('MathJax script loaded with enhanced delimiter support');
        if (window.MathJax && window.MathJax.startup) {
          window.MathJax.startup.promise.then(() => {
            console.log('MathJax startup complete');
            this.mathJaxLoaded = true;
            resolve();
          });
        } else {
          this.mathJaxLoaded = true;
          resolve();
        }
      };
      script.onerror = (error) => {
        console.error('Failed to load MathJax:', error);
        reject(error);
      };
      
      document.head.appendChild(script);

      setTimeout(() => {
        if (!this.mathJaxLoaded) {
          reject(new Error('MathJax loading timeout'));
        }
      }, 10000);
    });

    return this.mathJaxPromise;
  }

  static initializeMathLive() {
    if (this.mathLiveLoaded) return Promise.resolve();
    
    this.mathLivePromise = new Promise((resolve) => {
      try {
        if (window.MathfieldElement || typeof MathfieldElement !== 'undefined') {
          this.mathLiveLoaded = true;
          resolve();
        } else {
          this.mathLiveLoaded = true;
          resolve();
        }
      } catch (error) {
        console.log('MathLive will be loaded by individual components');
        this.mathLiveLoaded = true;
        resolve();
      }
    });

    return this.mathLivePromise;
  }

  static async renderMath(element) {
    if (!element) return;

    try {
      await this.initializeMathJax();
      
      if (!window.MathJax) {
        console.warn('MathJax not available. Math expressions will not be rendered.');
        return;
      }

      if (window.MathJax.typesetPromise) {
        await window.MathJax.typesetPromise([element]);
      } else if (window.MathJax.typeset) {
        window.MathJax.typeset([element]);
      }
    } catch (error) {
      console.error('Error rendering math:', error);
    }
  }

  /**
   * Process math expressions in content for proper rendering
   * Handles both dollar-sign delimited expressions and \[ \] notation
   */
  static processMathExpressions(content) {
    if (!content) return content;
    
    let processedContent = content;
    
    // Convert \[ \] style display math to $$ $$ format
    processedContent = processedContent.replace(/\\\[([\s\S]*?)\\\]/g, (match, inner) => {
      return `$$${inner.trim()}$$`;
    });
    
    // Convert \( \) style inline math to $ $ format
    processedContent = processedContent.replace(/\\\(([\s\S]*?)\\\)/g, (match, inner) => {
      return `$${inner.trim()}$`;
    });
    
    // Handle \displaylines environment
    processedContent = processedContent.replace(
      /\$\\displaylines\{([\s\S]*?)\}\$/g,
      (match, innerContent) => {
        const lines = innerContent.split('\\\\');
        return lines.map(line => `$$${line.trim()}$$`).join('\n\n');
      }
    );
    
    return processedContent;
  }

  /**
   * Check if a string contains LaTeX expressions
   */
  static containsMathExpressions(content) {
    if (!content) return false;
    
    const latexPatterns = [
      /\$\$.+?\$\$/s,
      /\$.+?\$/s,
      /\\\[[\s\S]+?\\\]/s,
      /\\\([\s\S]+?\\\)/s,
      /\\begin\{.+?\}.+?\\end\{.+?\}/s,
      /\\frac\{.+?\}\{.+?\}/s,
      /\\sum/,
      /\\int/,
      /\\prod/,
      /\\sqrt/,
      /\\mathbb/,
      /\\mathcal/,
      /\\mathrm/,
      /\\mathbf/,
      /\\mathit/,
      /\\text/,
      /\\alpha|\\beta|\\gamma|\\delta|\\epsilon|\\zeta|\\eta|\\theta|\\iota|\\kappa|\\lambda|\\mu|\\nu|\\xi|\\pi|\\rho|\\sigma|\\tau|\\upsilon|\\phi|\\chi|\\psi|\\omega/
    ];
    
    return latexPatterns.some(pattern => pattern.test(content));
  }

  static processContent(text) {
    console.log('Processing content:', text);
    
    // First normalize delimiters to dollar signs
    const normalized = this.processMathExpressions(text);
    
    // Then protect all LaTeX expressions from markdown processing
    const processedText = this.protectMathExpressions(normalized);
    
    // Process markdown
    const processedMarkdown = this.processMarkdown(processedText);
    
    // Restore LaTeX expressions
    return this.restoreMathExpressions(processedMarkdown);
  }

  static protectMathExpressions(text) {
    let result = text;
    
    // Protect display math (order matters - do display math first)
    result = result.replace(/\$\$([\s\S]*?)\$\$/g, (match) => {
      return `<DISPLAYMATH>${encodeURIComponent(match)}</DISPLAYMATH>`;
    });
    
    result = result.replace(/\\displaylines\{([\s\S]*?)\}/g, (match, content) => {
      return `<DISPLAYMATH>${encodeURIComponent(`\\[${content}\\]`)}</DISPLAYMATH>`;
    });
    
    result = result.replace(/\\\[([\s\S]*?)\\\]/g, (match) => {
      return `<DISPLAYMATH>${encodeURIComponent(match)}</DISPLAYMATH>`;
    });
    
    // Protect inline math
    result = result.replace(/\\\(([\s\S]*?)\\\)/g, (match) => {
      return `<INLINEMATH>${encodeURIComponent(match)}</INLINEMATH>`;
    });
    
    // Protect dollar inline math (be careful not to match display math)
    result = result.replace(/(?<!\$)\$(?!\$)((?:[^$]|\\\$)+?)\$(?!\$)/g, (match) => {
      return `<INLINEMATH>${encodeURIComponent(match)}</INLINEMATH>`;
    });
    
    return result;
  }

  static escapeHtml(str) {
    if (!str) return str;
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static restoreMathExpressions(text) {
    let result = text;

    // Restore display math
    result = result.replace(/<DISPLAYMATH>(.*?)<\/DISPLAYMATH>/g, (match, encoded) => {
      // decode, then escape HTML so `<` and `>` are safe in the final HTML string
      const decoded = decodeURIComponent(encoded);
      return this.escapeHtml(decoded);
    });

    // Restore inline math
    result = result.replace(/<INLINEMATH>(.*?)<\/INLINEMATH>/g, (match, encoded) => {
      const decoded = decodeURIComponent(encoded);
      return this.escapeHtml(decoded);
    });

    return result;
  }

  static async loadMarked() {
    if (window.marked) return;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/marked@9.1.6/marked.min.js';
      script.onload = () => {
        console.log('Marked.js loaded');
        resolve();
      };
      script.onerror = (error) => {
        console.error('Failed to load marked.js:', error);
        reject(error);
      };
      
      document.head.appendChild(script);
    });
  }

  static processMarkdown(text) {
    if (!text) return '';

    try {
      if (window.marked) {
        if (window.marked.setOptions) {
          window.marked.setOptions({
            breaks: true,
            gfm: true,
            sanitize: false,
            smartLists: true,
            smartypants: false
          });
        }
        
        return window.marked.parse ? window.marked.parse(text) : window.marked(text);
      } else {
        return this.basicMarkdownProcessing(text);
      }
    } catch (error) {
      console.error('Error processing markdown:', error);
      return this.basicMarkdownProcessing(text);
    }
  }

  static basicMarkdownProcessing(text) {
    return text
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>');
  }

  static async processContentWithMarkdown(text) {
    if (!text) return '';

    try {
      await this.loadMarked();
      return this.processContent(text);
    } catch (error) {
      console.error('Error processing content with markdown:', error);
      return text.replace(/\n/g, '<br>');
    }
  }

  // Enhanced math content detection (alias for containsMathExpressions)
  static hasMathContent(text) {
    return this.containsMathExpressions(text);
  }

  static isMathJaxLoaded() {
    return this.mathJaxLoaded && window.MathJax && window.MathJax.typesetPromise;
  }

  static isMathLiveLoaded() {
    return this.mathLiveLoaded;
  }
}